"use client";

import { createContext, useContext, useState, useCallback, useEffect } from "react";

interface HelpContextValue {
  helpEnabled: boolean;
  toggleHelp: () => void;
  openChatWithAI: (question: string) => void;
}

const HelpContext = createContext<HelpContextValue>({
  helpEnabled: false,
  toggleHelp: () => {},
  openChatWithAI: () => {},
});

export function HelpProvider({ children }: { children: React.ReactNode }) {
  // Starts false (SSR-safe default, consumed unconditionally by the header's
  // help-toggle button on every page via app-shell.tsx). Reading localStorage
  // in the initializer meant the server always rendered the "off" classes/
  // aria-pressed while the client's first paint could read "on" for anyone
  // who'd previously enabled Help Mode — a hydration mismatch. Real value is
  // applied in the effect below, after hydration.
  const [helpEnabled, setHelpEnabled] = useState<boolean>(false);
  useEffect(() => {
    try {
      if (localStorage.getItem("bb-help-tooltips") === "true") setHelpEnabled(true);
    } catch { /* ignore */ }
  }, []);

  const toggleHelp = useCallback(() => {
    setHelpEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("bb-help-tooltips", String(next));
      } catch {}
      return next;
    });
  }, []);

  /**
   * Opens the chat window and navigates to the AI Advisor tab with a pre-filled question.
   * Components render a "Ask BB AI" button in their help bubble that calls this.
   */
  const openChatWithAI = useCallback((question: string) => {
    window.dispatchEvent(
      new CustomEvent("open-chat-window", {
        detail: { tab: "advisor", prefillQuestion: question },
      })
    );
  }, []);

  return (
    <HelpContext.Provider value={{ helpEnabled, toggleHelp, openChatWithAI }}>
      {children}
    </HelpContext.Provider>
  );
}

export const useHelp = () => useContext(HelpContext);
